import axios from 'axios';

class HttpChatService {
  constructor() {
    // Use environment variable (now properly set in Vercel dashboard)
    const envBackendUrl = process.env.REACT_APP_BACKEND_URL;
    const fallbackUrl = process.env.NODE_ENV === 'production'
      ? 'https://yoga-chatbot-api.onrender.com'
      : 'http://localhost:5002';

    // Clean up environment variable (remove newlines and trailing slashes)
    this.baseURL = (envBackendUrl || fallbackUrl).replace(/[\n\r]/g, '').replace(/\/$/, '');

    console.log('HttpChatService: Backend URL configured as:', this.baseURL);
    console.log('HttpChatService: Environment:', process.env.REACT_APP_ENV);
    console.log('HttpChatService: Node Environment:', process.env.NODE_ENV);
    console.log('HttpChatService: Raw env var:', process.env.REACT_APP_BACKEND_URL);
    console.log('HttpChatService: Using fallback URL:', !envBackendUrl);

    this.sessionId = null;
    this.sessionToken = null;
    this.listeners = new Map();
    this.isConnected = false;
    this.isCreatingSession = false;

    // Configure axios defaults
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Initialize connection (HTTP doesn't need persistent connection)
  async connect() {
    console.log('HttpChatService: Attempting to connect to:', this.baseURL);
    console.log('HttpChatService: Environment variables:', {
      REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_ENV: process.env.REACT_APP_ENV
    });

    // Test connection with a simple health check first
    try {
      console.log('HttpChatService: Testing connection with health check...');
      const healthResponse = await this.api.get('/api/health');
      console.log('HttpChatService: Health check successful:', healthResponse.data);
    } catch (error) {
      console.error('HttpChatService: Health check failed:', error);
      throw new Error(`Backend health check failed: ${error.message}`);
    }

    this.isConnected = true;
    this.emit('connect');
    console.log('HttpChatService: Connection established');
    return Promise.resolve();
  }

  // Disconnect (cleanup)
  disconnect() {
    this.isConnected = false;
    this.sessionId = null;
    this.sessionToken = null;
    this.listeners.clear();
    this.emit('disconnect');
  }

  // Create or join a session
  async joinSession(sessionId = null) {
    // Prevent multiple session creation
    if (this.sessionId) {
      console.log('HttpChatService: Session already exists:', this.sessionId);
      this.emit('session_joined', {
        sessionId: this.sessionId,
        messages: [],
        context: {},
        welcomeMessage: "Welcome back! How can I help you with yoga today?"
      });
      return;
    }

    if (this.isCreatingSession) {
      console.log('HttpChatService: Session creation already in progress...');
      return;
    }

    this.isCreatingSession = true;
    console.log('HttpChatService: Creating new session...');

    try {
      // Always create a new session for HTTP mode (simpler approach)
      const response = await this.api.post('/api/chat/session', {});
      console.log('HttpChatService: Session created successfully:', response.data);

      this.sessionId = response.data.sessionId;
      this.sessionToken = response.data.sessionToken || null;
      this.emit('session_joined', {
        sessionId: this.sessionId,
        messages: [],
        context: {},
        welcomeMessage: response.data.welcomeMessage
      });
    } catch (error) {
      console.error('HttpChatService: Failed to join session:', error);
      console.error('HttpChatService: Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });

      // Provide more specific error messages
      let errorMessage = 'Failed to join session';
      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error - unable to reach chat server';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required - deployment protection may be enabled';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error - please try again later';
      }

      this.emit('error', { message: errorMessage });
      throw new Error(errorMessage);
    } finally {
      this.isCreatingSession = false;
    }
  }

  // Send a message
  async sendMessage(message, type = 'text', options = {}) {
    if (!this.sessionId) {
      throw new Error('No active session');
    }

    if (!message || typeof message !== 'string' || message.trim().length === 0) {
      throw new Error('Invalid message');
    }

    try {
      // Emit user message immediately for better UX
      const userMessage = {
        id: Date.now().toString(),
        content: message.trim(),
        sender: 'user',
        type: type,
        timestamp: new Date().toISOString()
      };
      this.emit('message', userMessage);

      // Show typing indicator
      this.emit('typing', { isTyping: true });

      // Prepare context with language override
      const currentContext = this.getStoredContext();
      if (options.language) {
        if (!currentContext.preferences) {
          currentContext.preferences = {};
        }
        currentContext.preferences.language = options.language;
      }

      // Send message to backend
      const response = await this.api.post('/api/chat/message', {
        message: message.trim(),
        sessionId: this.sessionId,
        context: {
          userPreferences: currentContext.preferences,
          ...currentContext.context
        }
      });

      if (response.data.sessionToken) {
        this.sessionToken = response.data.sessionToken;
      }

      // Hide typing indicator
      this.emit('typing', { isTyping: false });

      // Emit bot response
      const botMessage = {
        id: response.data.messageId || Date.now().toString(),
        content: response.data.response,
        sender: 'bot',
        type: 'text',
        timestamp: new Date().toISOString(),
        metadata: response.data.metadata || {},
        suggestions: response.data.suggestions || []
      };
      this.emit('message', botMessage);

      return response.data;
    } catch (error) {
      // Hide typing indicator on error
      this.emit('typing', { isTyping: false });

      console.error('Failed to send message:', error);

      // Emit error message
      const errorMessage = {
        id: Date.now().toString(),
        content: 'Sorry, I\'m having trouble processing your message right now. Please try again.',
        sender: 'bot',
        type: 'text',
        timestamp: new Date().toISOString(),
        suggestions: [
          'Try asking again',
          'Tell me about yoga classes',
          'What are the benefits of meditation?'
        ]
      };
      this.emit('message', errorMessage);

      throw error;
    }
  }

  // Get stored context from localStorage
  getStoredContext() {
    try {
      const preferences = localStorage.getItem('yoga-chat-user-preferences');
      const context = localStorage.getItem('yoga-chat-conversation-context');

      return {
        preferences: preferences ? JSON.parse(preferences) : null,
        context: context ? JSON.parse(context) : null
      };
    } catch (error) {
      console.error('Error parsing stored context:', error);
      return {};
    }
  }

  // End session
  async endSession() {
    if (!this.sessionId) {
      return;
    }

    try {
      await this.api.delete(`/api/chat/session/${this.sessionId}`, {
        headers: this.sessionToken ? { 'x-session-token': this.sessionToken } : {}
      });
      this.sessionId = null;
      this.sessionToken = null;
    } catch (error) {
      console.error('Failed to end session:', error);
    }
  }

  // Get chat statistics (for monitoring)
  async getStats() {
    try {
      const response = await this.api.get('/api/chat/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to get stats:', error);
      return null;
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.api.get('/api/chat/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      return { status: 'unhealthy', error: error.message };
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Utility methods
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      sessionId: this.sessionId,
      type: 'http'
    };
  }

  isHealthy() {
    return this.isConnected;
  }

  // Typing indicators (no-op for HTTP, but kept for compatibility)
  setTyping(isTyping) {
    // HTTP doesn't support real-time typing indicators
    // This is a no-op for compatibility with socket service
  }

  // Ping (health check)
  async ping() {
    try {
      const response = await this.api.get('/api/health');
      this.emit('pong');
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }
}

// Create singleton instance
const httpChatService = new HttpChatService();

export default httpChatService;
